import { useState, useEffect, useCallback } from 'react';
import { fetchApi } from '../lib/api';

const CATEGORIES = [
  { value: 'action_figures', label: 'Action Figures' },
  { value: 'arts_crafts', label: 'Arts & Crafts' },
  { value: 'board_games', label: 'Board Games' },
  { value: 'card_games', label: 'Card Games' },
  { value: 'construction', label: 'Construction' },
  { value: 'dolls', label: 'Dolls' },
  { value: 'educational', label: 'Educational' },
  { value: 'electronic', label: 'Electronic' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'plush', label: 'Plush' },
  { value: 'pretend_play', label: 'Pretend Play' },
  { value: 'puzzles', label: 'Puzzles' },
  { value: 'ride_on', label: 'Ride-On' },
  { value: 'sensory', label: 'Sensory' },
  { value: 'stem', label: 'STEM' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'other', label: 'Other' },
];

const AGE_RANGES = [
  { value: '0-2', label: '0–2 years' },
  { value: '3-5', label: '3–5 years' },
  { value: '6-8', label: '6–8 years' },
  { value: '8-12', label: '8–12 years' },
  { value: '13+', label: '13+ years' },
  { value: 'all_ages', label: 'All Ages' },
];

const PRICE_POINTS = [
  { value: 'value', label: 'Value', range: 'Under $10' },
  { value: 'mid', label: 'Mid', range: '$10 – $25' },
  { value: 'premium', label: 'Premium', range: '$25 – $50' },
  { value: 'deluxe', label: 'Deluxe', range: '$50+' },
];

const MECHANICS = [
  { value: 'strategy', label: 'Strategy' },
  { value: 'dexterity', label: 'Dexterity' },
  { value: 'creativity', label: 'Creativity' },
  { value: 'collection', label: 'Collection' },
  { value: 'role_play', label: 'Role Play' },
  { value: 'physical_activity', label: 'Physical Activity' },
  { value: 'problem_solving', label: 'Problem Solving' },
  { value: 'social_deduction', label: 'Social Deduction' },
  { value: 'compound_play', label: 'Compound Play' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  company_description: '',
  categories_seeking: [],
  age_ranges_seeking: [],
  price_points_seeking: [],
  mechanics_seeking: [],
  wishlist_summary: '',
  wishlist_avoid: '',
  wishlist_notes: '',
};

function ToggleChip({ selected, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all border-2 flex items-center gap-1.5 ${
        selected
          ? 'bg-amber-50 border-vault-gold text-vault-gold'
          : 'bg-white border-vault-border text-gray-500 hover:border-gray-300'
      }`}
    >
      {selected && <span className="text-xs">✓</span>}
      {label}
    </button>
  );
}

export default function WishListBuilder() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved' | 'error'
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [savedData, setSavedData] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const loadWishlist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi('/api/company-profile/wishlist');
      const w = res.wishlist || {};
      const data = {
        company_description: w.company_description || '',
        categories_seeking: w.categories_seeking || [],
        age_ranges_seeking: w.age_ranges_seeking || [],
        price_points_seeking: w.price_points_seeking || [],
        mechanics_seeking: w.mechanics_seeking || [],
        wishlist_summary: w.wishlist_survey?.summary || '',
        wishlist_avoid: w.wishlist_survey?.avoid || '',
        wishlist_notes: w.wishlist_survey?.notes || '',
      };
      setFormData(data);
      setSavedData(JSON.stringify(data));

      // Show onboarding if everything is empty
      const isEmpty = !data.company_description && data.categories_seeking.length === 0
        && data.age_ranges_seeking.length === 0 && data.mechanics_seeking.length === 0
        && !data.wishlist_summary;
      if (isEmpty && !localStorage.getItem('pv_wishlist_onboarding_dismissed')) {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error('Load wishlist error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadWishlist(); }, [loadWishlist]);

  // Dirty state
  const isDirty = savedData !== null && JSON.stringify(formData) !== savedData;

  // Warn on navigation away
  useEffect(() => {
    const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    if (isDirty) window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const update = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setSaveStatus(null);
  };

  const toggleArray = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value],
    }));
    setSaveStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveStatus(null);
    try {
      await fetchApi('/api/company-profile/wishlist', {
        method: 'PUT',
        body: JSON.stringify({
          company_description: formData.company_description,
          categories_seeking: formData.categories_seeking,
          age_ranges_seeking: formData.age_ranges_seeking,
          price_points_seeking: formData.price_points_seeking,
          mechanics_seeking: formData.mechanics_seeking,
          wishlist_survey: {
            summary: formData.wishlist_summary,
            avoid: formData.wishlist_avoid,
            notes: formData.wishlist_notes,
          },
        }),
      });
      setSavedData(JSON.stringify(formData));
      setSaveStatus('saved');
    } catch (err) {
      setSaveError(err.message);
      setSaveStatus('error');
    }
    setSaving(false);
  };

  // Completion checklist
  const sections = [
    { label: 'Company Description', done: !!formData.company_description },
    { label: 'Categories', done: formData.categories_seeking.length > 0 },
    { label: 'Age Ranges', done: formData.age_ranges_seeking.length > 0 },
    { label: 'Price Points', done: formData.price_points_seeking.length > 0 },
    { label: 'Play Mechanics', done: formData.mechanics_seeking.length > 0 },
    { label: 'Wish List Details', done: !!formData.wishlist_summary },
  ];
  const completedCount = sections.filter(s => s.done).length;

  const labelCls = "block font-heading text-lg text-vault-navy mb-1";
  const subCls = "text-sm text-gray-400 mb-4";
  const textareaCls = "w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors resize-none";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-gray-400">Loading wish list...</p>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-[28px] text-vault-navy">Your Wish List</h1>
        <p className="text-sm text-gray-400 mt-1">
          Tell our AI what you're looking for. The more specific you are, the better your inventor matches will be.
        </p>
      </div>

      {/* Empty state banner */}
      {completedCount === 0 && (
        <div className="mb-5 px-5 py-4 rounded-xl bg-amber-50 border-2 border-vault-gold/30">
          <p className="text-sm text-amber-700 font-medium">
            Your wish list is empty — inventors cannot be matched to your company until you complete this form.
          </p>
        </div>
      )}

      {/* Onboarding card */}
      {showOnboarding && (
        <div className="mb-5 glass p-6 border-2 border-vault-gold/30">
          <h2 className="font-heading text-lg text-vault-navy mb-2">Let's set up your wish list</h2>
          <p className="text-sm text-gray-400 mb-4">
            Inventors on PlayVentr are looking for companies to pitch. Complete your wish list so our AI can surface the ideas most likely to fit your line.
          </p>
          <div className="space-y-2 mb-4">
            {['Describe your company', 'Select your categories and preferences', 'Save — inventors start matching immediately'].map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                <span className="w-5 h-5 rounded-full bg-vault-gold/10 text-vault-gold text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                {step}
              </div>
            ))}
          </div>
          <button
            onClick={() => { setShowOnboarding(false); localStorage.setItem('pv_wishlist_onboarding_dismissed', '1'); }}
            className="text-sm font-semibold text-vault-gold hover:underline"
          >
            Got it, let's go
          </button>
        </div>
      )}

      {/* Completion indicator */}
      <div className="glass p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-vault-navy">{completedCount} of {sections.length} sections complete</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {sections.map(s => (
            <div key={s.label} className="flex items-center gap-1.5 text-xs">
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                s.done ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-500'
              }`}>
                {s.done ? '✓' : '○'}
              </span>
              <span className={s.done ? 'text-gray-500' : 'text-gray-400'}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 1 — Company Description */}
      <div className="glass p-7 mb-5">
        <label className={labelCls}>About Your Company</label>
        <p className={subCls}>Describe what your company does, your brand positioning, and what makes a great fit for your line.</p>
        <textarea
          className={textareaCls}
          rows={4}
          maxLength={500}
          placeholder="e.g. We're a specialty STEM toy company focused on ages 6-12. Our products retail between $25-$50 and we prioritize educational value with fun play patterns..."
          value={formData.company_description}
          onChange={e => update('company_description', e.target.value)}
        />
        <div className="text-right text-xs text-gray-300 mt-1">{formData.company_description.length} / 500</div>
      </div>

      {/* SECTION 2 — Categories */}
      <div className="glass p-7 mb-5">
        <label className={labelCls}>Product Categories</label>
        <p className={subCls}>Select all categories you are actively looking for.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {CATEGORIES.map(cat => (
            <ToggleChip
              key={cat.value}
              selected={formData.categories_seeking.includes(cat.value)}
              label={cat.label}
              onClick={() => toggleArray('categories_seeking', cat.value)}
            />
          ))}
        </div>
      </div>

      {/* SECTION 3 — Age Ranges */}
      <div className="glass p-7 mb-5">
        <label className={labelCls}>Target Age Ranges</label>
        <p className={subCls}>Which age groups do your products serve?</p>
        <div className="flex flex-wrap gap-2">
          {AGE_RANGES.map(age => (
            <ToggleChip
              key={age.value}
              selected={formData.age_ranges_seeking.includes(age.value)}
              label={age.label}
              onClick={() => toggleArray('age_ranges_seeking', age.value)}
            />
          ))}
        </div>
      </div>

      {/* SECTION 4 — Price Points */}
      <div className="glass p-7 mb-5">
        <label className={labelCls}>Price Points</label>
        <p className={subCls}>What retail price tiers are you targeting?</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PRICE_POINTS.map(pp => {
            const selected = formData.price_points_seeking.includes(pp.value);
            return (
              <button
                key={pp.value}
                type="button"
                onClick={() => toggleArray('price_points_seeking', pp.value)}
                className={`relative p-4 rounded-xl text-center transition-all border-2 ${
                  selected ? 'border-vault-gold bg-amber-50' : 'border-vault-border bg-white hover:border-gray-300'
                }`}
              >
                {selected && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-vault-gold text-white text-[10px] flex items-center justify-center">✓</span>
                )}
                <div className={`text-sm font-semibold ${selected ? 'text-vault-gold' : 'text-vault-navy'}`}>{pp.label}</div>
                <div className="text-xs text-gray-400 mt-0.5">{pp.range}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* SECTION 5 — Play Mechanics */}
      <div className="glass p-7 mb-5">
        <label className={labelCls}>Play Mechanics</label>
        <p className={subCls}>What kinds of play patterns are you looking for?</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {MECHANICS.map(m => (
            <ToggleChip
              key={m.value}
              selected={formData.mechanics_seeking.includes(m.value)}
              label={m.label}
              onClick={() => toggleArray('mechanics_seeking', m.value)}
            />
          ))}
        </div>
      </div>

      {/* SECTION 6 — Wish List Details */}
      <div className="glass p-7 mb-5">
        <label className={labelCls}>Detailed Wish List</label>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-vault-navy mb-1.5">In your own words, what are you looking for?</label>
            <textarea
              className={textareaCls}
              rows={4}
              maxLength={1000}
              placeholder="e.g. We're especially excited about outdoor active play for ages 6-10, anything that gets kids moving. We love novelty mechanics we haven't seen before..."
              value={formData.wishlist_summary}
              onChange={e => update('wishlist_summary', e.target.value)}
            />
            <div className="text-right text-xs text-gray-300 mt-1">{formData.wishlist_summary.length} / 1000</div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-vault-navy mb-1.5">What should inventors NOT pitch to you?</label>
            <textarea
              className={textareaCls}
              rows={3}
              maxLength={500}
              placeholder="e.g. No licensed IP, no screen-based products, nothing that requires batteries for core play..."
              value={formData.wishlist_avoid}
              onChange={e => update('wishlist_avoid', e.target.value)}
            />
            <div className="text-right text-xs text-gray-300 mt-1">{formData.wishlist_avoid.length} / 500</div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-vault-navy mb-1.5">Anything else inventors should know?</label>
            <textarea
              className={textareaCls}
              rows={3}
              maxLength={500}
              placeholder="e.g. We attend Toy Fair every year. We prefer ideas that are prototype-ready. We offer 5% royalties..."
              value={formData.wishlist_notes}
              onChange={e => update('wishlist_notes', e.target.value)}
            />
            <div className="text-right text-xs text-gray-300 mt-1">{formData.wishlist_notes.length} / 500</div>
          </div>
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-[220px] bg-white border-t border-vault-border px-5 py-3 z-40">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            {isDirty && (
              <>
                <span className="w-2 h-2 rounded-full bg-vault-gold" />
                <span className="text-vault-gold font-medium">Unsaved changes</span>
              </>
            )}
            {saveStatus === 'saved' && !isDirty && (
              <>
                <span className="text-emerald-500">✓</span>
                <span className="text-emerald-600 font-medium">Wish list saved</span>
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <span className="text-vault-error">✕</span>
                <span className="text-vault-error font-medium">{saveError}</span>
              </>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-40 transition-all"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {saving ? 'Saving...' : 'Save Wish List'}
          </button>
        </div>
      </div>
    </div>
  );
}
